import { FC, useContext } from "react";
import { TableSkeleton } from "../../components/Skeleton/TableSkeleton";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { DataTable } from "../../components/ui/data-table";
import { Label } from "../../components/ui/label";
import {
  capitalizeWords,
  formatPhoneNumber,
  formatUniversalNumber,
} from "../../lib/utils";
import { CustomerContext } from "../../providers/CustomerProvider";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Input } from "../../components/ui/input";
import { Search } from "lucide-react";

// Get initials for avatar
const getInitials = (name: string) => {
  if (!name || name === "-") return "BZ";

  const parts = name.split(" ").filter((part) => part && part !== "-");
  if (parts.length === 0) return "BZ";

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const columns = [
  {
    accessorKey: "customer_name",
    header: "Customer Name",
    cell: ({ row }: any) => {
      const firstName = row.original.firstName || "-";
      const lastName = row.original.lastName || "";

      // If both names are missing or just "-", use "BZ"
      if ((firstName === "-" && !lastName) || (!firstName && !lastName)) {
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8 bg-[hsl(214,94.6%,85%)] text-[hsl(214,94.6%,30%)]">
              <AvatarFallback>BZ</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">BookEZ</div>
            </div>
          </div>
        );
      }

      // If only first name exists, use that
      const displayName = lastName
        ? `${capitalizeWords(firstName)} ${capitalizeWords(lastName)}`.trim()
        : capitalizeWords(firstName);

      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 bg-[hsl(214,94.6%,85%)] text-[hsl(214,94.6%,30%)]">
            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{displayName || "BookEZ"}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "companyName",
    header: "Company Name",
    cell: ({ row }: any) => {
      const companyName = row.original.companyName || "-"; // Show "-" if value is empty

      // Capitalize each word in company name
      const capitalizedCompanyName = capitalizeWords(companyName);

      return <span>{capitalizedCompanyName}</span>;
    },
  },
  {
    accessorKey: "phoneNumber",
    header: "Phone No",
    cell: ({ row }: any) => {
      const phoneNumber =
        row.original.phoneNumber || row.original.mobileNumber || "-"; // Show "-" if value is empty

      // Format the phone number to be more readable
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber);

      return <span>{formattedPhoneNumber}</span>;
    },
  },
  {
    accessorKey: "email",
    header: "Email ID",
    cell: ({ row }: any) => {
      const email = row.original.email || "-"; // Show "-" if value is empty
      return <span>{email}</span>;
    },
  },
  {
    accessorKey: "address1",
    header: "Address Details",
    size: 200,
    enableResizing: false,
    cell: ({ row }: any) => {
      const address1 = row.original.address1; // Show "-" if value is empty
      const address2 = row.original.address2; // If address2 is missing, we just ignore it

      const capitalizedAddress1 = capitalizeWords(address1);
      const capitalizedAddress2 = capitalizeWords(address2);

      // Concatenate address1 and address2 properly without unnecessary hyphens
      let fullAddress = "";

      if (capitalizedAddress1 && capitalizedAddress1 !== "-") {
        fullAddress = capitalizedAddress1;
      }

      if (capitalizedAddress2 && capitalizedAddress2 !== "-" && fullAddress) {
        fullAddress += ` - ${capitalizedAddress2}`;
      } else if (capitalizedAddress2 && capitalizedAddress2 !== "-") {
        fullAddress = capitalizedAddress2;
      }

      if (!fullAddress) {
        fullAddress = "-"; // Fallback when both addresses are empty
      }

      return <span>{fullAddress}</span>;
    },
  },
  {
    accessorKey: "balance",
    header: "Balance",
    cell: ({ row }: any) => {
      const balances = [
        {
          balanceType: "Outstanding",
          balanceAmt: row.original.totalOutstandingAmt,
        },
        ...row.original.customerBalance,
      ];
      // If customerBalance is an array:
      if (Array.isArray(balances)) {
        return (
          <div>
            {balances?.length > 0 ? (
              balances?.map((bal: any) => (
                <div key={bal.businessMasterId + "-" + bal.balanceTypeId}>
                  <span className="font-semibold">{bal.balanceType}:</span>{" "}
                  {formatUniversalNumber(bal.balanceAmt)}
                </div>
              ))
            ) : (
              <div className="flex justify-center">
                <span>-</span>
              </div>
            )}
          </div>
        );
      }
    },
  },
  // {
  //   accessorKey: "isActive",
  //   header: "Status",
  //   cell: ({ row }: any) => {
  //     const status = row.original.isActive ? "current" : "pastDue";
  //     const badgeStyles: any = {
  //       pastDue: "BookEZ_bg_green_light BookEZ_font_color_green",
  //       current: "bg-amber-100 BookEZ_font_color_yellow",
  //     };
  //     const statusLabel = status || "-"; // Show "-" if status is missing
  //     return (
  //       <div className="">
  //         <span
  //           className={`BookEZ_font_body_size  BookEZ_font_body_medium px-3 py-1 rounded-full ${badgeStyles[status]}`}
  //         >
  //           {statusLabel}
  //         </span>
  //       </div>
  //     );
  //   },
  // },
];

type CustomerTableProps = {
  title?: string;
  filter?: boolean;
  isSearch?: boolean;
};

export const CustomerTable: FC<CustomerTableProps> = ({
  title = "Customers",
  filter = true,
  isSearch = true,
}) => {
  const {
    isLoading,
    customersData,
    pagination,
    switchOn,
    setSwitchOn,
    handlePageSizeChange,
    handlePageChange,
    applyFilter,
    searchQuery,
    handleSearch,
    handleSearchClick,
  } = useContext(CustomerContext); // Using custom h
  return (
    <>
      <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
        <h1 className="BookEZ_font_heading BookEZ_font_bold BookEZ_font_header_color">
          {title}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          {isSearch && (
            <div className="relative">
              <Input
                type="text"
                id="search Customers"
                placeholder="Search Customers..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pr-10"
              />
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 BookEZ_font_color_gray_1 disabled:cursor-not-allowed"
                aria-hidden="true"
                onClick={() => {
                  handleSearchClick();
                }}
              />
            </div>
          )}
          {filter && (
            <div className="flex flex-wrap items-center gap-3">
              <div
                className={`${
                  switchOn ? "BookEZ_card_border_color" : ""
                } flex items-center gap-2 border BookEZ_card_border_color rounded-md BookEZ_card_bg p-2`}
              >
                <Label
                  htmlFor="archived"
                  className="BookEZ_font_body_size BookEZ_font_color_gray_3 cursor-pointer"
                >
                  Include Archived
                </Label>
                <Checkbox
                  id="archived"
                  checked={switchOn}
                  onCheckedChange={(checked) => {
                    setSwitchOn(!switchOn); // Toggle the switch state
                  }}
                />
              </div>
              <Button variant={"outline"} onClick={applyFilter}>
                Apply Filter
              </Button>
            </div>
          )}
        </div>
      </div>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          columns={columns}
          data={customersData}
          showPagination={true}
          pageSize={pagination.pageSize}
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRecords={pagination.totalRecords}
        />
      )}
    </>
  );
};
